import React, { MouseEvent, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Button } from '../../buttons';
import { ClickableCard } from '../../cards';
import { TagsFormFilterField, TagsFormFiltersInputTypes } from './types';
import { TagsFormPopover } from './tags-form-popover';
import { DynamicReactPortal } from '../../dynamic-react-portal';
import { calculatePopoverState, isHTMLElement } from '../../../../utils';

import './tags-form-filters.scss';

export interface TagsFormFiltersProps<T extends Record<string, any>> {
    fields: TagsFormFilterField[];
    onChange?: (filters: T) => void;
    searchText: string;
    values?: T;
}

export function TagsFormFilters<T extends Record<string, any>>({
    fields,
    onChange,
    searchText,
    values,
}: TagsFormFiltersProps<T>): React.ReactElement {
    const containerRef = useRef<HTMLDivElement>(null);
    const portalRef = useRef(null);
    const popoverRef = useRef(null);

    const [tempFilters, setTempFilters] = useState<T>(values || ({} as T));

    const [popupIsVisible, setPopupIsVisible] = useState(false);

    useEffect(() => {
        values && setTempFilters(values);
    }, [values]);

    useEffect(() => {
        if (popupIsVisible) {
            calculatePopoverState(containerRef.current, popoverRef.current, '.tags-form-filters__tag-input');
            return;
        }

        if (popoverRef.current) {
            popoverRef.current.style.top = '';
            popoverRef.current.style.bottom = '';
            popoverRef.current.style.visibility = 'hidden'; // Try to prevent flicker
        }
    }, [popupIsVisible]);

    const handleFieldChange = (key: string, value: any) => {
        const fieldType = fields.find((item) => item.key === key)?.type;
        if (fieldType === TagsFormFiltersInputTypes.CHECKBOX && value === false) {
            const newFilters = { ...tempFilters };
            delete newFilters[key];
            setTempFilters(newFilters);
        } else {
            setTempFilters({
                ...tempFilters,
                ...{ [key]: value },
            });
        }
    };

    const applyFilters = () => {
        onChange && onChange(tempFilters);
        setPopupIsVisible(false);
    };

    const clearFilters = () => {
        onChange && onChange({} as T);
    };

    const getLabelFor = (key: string) => {
        return fields.find((item) => item.key === key)?.label;
    };

    const clearField = (key: string) => {
        const newFilters = { ...tempFilters };
        delete newFilters[key];
        onChange && onChange(newFilters);
    };

    const togglePopupOnClick = (evt: MouseEvent) => {
        evt.stopPropagation();
        evt.preventDefault();
        setPopupIsVisible((oldValue) => !oldValue);
    };

    const findActualFieldInWrapper = (wrapper: HTMLElement): HTMLElement => {
        if (wrapper.classList.contains('.tags-form-popover__field-wrapper--dropdown')) {
            const dropdown = wrapper.querySelector('.select-dropdown');
            return isHTMLElement(dropdown) ? dropdown : null;
        }

        const input = wrapper.querySelector('input');
        return isHTMLElement(input) ? input : null;
    };

    const renderSearchBar = () => {
        const classes = classNames('tags-form-filters__tag-input', {
            'tags-form-filters__tag-input--active': popupIsVisible,
        });
        const activeKeys = Object.keys(values).filter((key) => values[key]);

        return (
            <ClickableCard
                testId="tags-form-filters-search-bar"
                aria-haspopup="dialog"
                aria-controls="advanced-search-form-popup"
                className={classes}
                onClick={togglePopupOnClick}
            >
                <i className="fa-kit fa-prizeout-search" />
                <ul className="tags-form-filters__tag-input-tags">
                    {activeKeys.length === 0 && <span>{searchText}</span>}
                    {activeKeys.length > 0 &&
                        activeKeys.map((key) => {
                            return (
                                <li className="tags-form-filters__tag-input-tag" key={`input-tag-${key}`}>
                                    <strong>{getLabelFor(key)}</strong>
                                    <span>{values[key]}</span>
                                    <Button
                                        isElement
                                        icon="fa-solid fa-circle-xmark"
                                        onClick={() => {
                                            clearField(key);
                                        }}
                                        testId={`clear-${key}`}
                                    />
                                </li>
                            );
                        })}
                </ul>
            </ClickableCard>
        );
    };

    const renderPopup = () => (
        <DynamicReactPortal ref={portalRef} portalId="tags-form-portal">
            <TagsFormPopover
                ref={popoverRef}
                shown={popupIsVisible}
                fields={fields}
                closePopover={() => setPopupIsVisible(false)}
                handleFieldChange={handleFieldChange}
                applyFilters={applyFilters}
                clearFilters={clearFilters}
                tempFilters={tempFilters}
            />
        </DynamicReactPortal>
    );

    const captureMouseHandler = (evt: MouseEvent | React.MouseEvent<HTMLElement>) => {
        evt.stopPropagation();
    };

    const captureMouse = {
        onMouseDown: captureMouseHandler,
        onMouseUp: captureMouseHandler,
    };

    const handleOnKeyEvent = (evt: React.KeyboardEvent) => {
        if (!popupIsVisible) {
            return;
        }

        if (evt.key === 'Tab') {
            if (evt.type === 'keyup') {
                return evt.preventDefault();
            }

            if (popoverRef.current && isHTMLElement(popoverRef.current)) {
                const firstWrapper = popoverRef.current.querySelector('.tags-form-popover__field-wrapper');

                if (isHTMLElement(firstWrapper)) {
                    const actualField = findActualFieldInWrapper(firstWrapper);

                    if (actualField) {
                        actualField.focus();
                    }
                }
            }

            evt.preventDefault();
        }
    };

    const handleKeys = {
        onKeyDown: handleOnKeyEvent,
        onKeyUp: handleOnKeyEvent,
    };

    return (
        <div className="tags-form-filters" ref={containerRef} {...captureMouse} {...handleKeys}>
            {renderSearchBar()}
            <div className="tags-form-filters__popup-container">{renderPopup()}</div>
        </div>
    );
}
